import { monthName } from '../lib/i18n/format'
import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Input } from '@heroui/react'
import { api } from '../lib/api'
import { explain } from '../lib/staffErrors'
import { settingCheck, settingInWords } from '../lib/settingWords'
import type { SettingView } from '../lib/types'
import { useStaffText } from '../ui/StaffText'
import { useFlash } from './sectionKit'
import { ChoiceSelect, Notice, PageHead, SettingRow, SettingsCard } from './settingsKit'
import '../styles/orgset.css'
import '../styles/install.css'

/**
 * Installation settings.
 *
 * A handful of values, and the whole design is about one distinction: a setting either
 * follows the configuration file or has been decided here. The file is still shown
 * when it is being overridden, because the question somebody actually asks at this
 * screen is "what was it before, and what happens if I put it back".
 *
 * Nothing here is a slider or a set of preset lengths. The values are written the
 * way the configuration file writes them — 12h, 90m, 0 — so that the person on the
 * telephone to support is quoting the same string the engineer is reading.
 *
 * These are the server's settings, not one organisation's: on a machine hosting two
 * organisations there is one session cookie and one idle timer, so one answer here.
 *
 * Each row edits in place rather than in a drawer: the box is one value, and a
 * drawer around one box would be more screen than setting.
 */

interface Described {
  group: string
  label: string
  /** One short line, only where getting it wrong causes harm. */
  says: string
}

const DESCRIBED: Record<string, Described> = {
  'auth.session_lifetime': { group: 'Sessions', label: 'Session length', says: '' },
  'auth.idle_timeout': { group: 'Sessions', label: 'Idle sign-out', says: 'Main displays and kiosks are exempt.' },
  // Off is the default for a reason: anybody who knows a username can lock that
  // person out by guessing. A locked account is never named at sign-in.
  'auth.lockout_after': {
    group: 'Wrong passwords',
    label: 'Lock after failures',
    says: 'Anyone knowing a username can lock it out.',
  },
  // 0 is what some audits ask for; it also means a teller locked out by somebody
  // else waits for a phone call.
  'auth.lockout_for': {
    group: 'Wrong passwords',
    label: 'Lock duration',
    says: '0 keeps it locked until an administrator unlocks.',
  },
  // The account of somebody who left and was never disabled. Counted from the last
  // sign-in, unlock or reset, or the upgrade that added this, whichever is latest.
  'auth.dormant_after_days': {
    group: 'Account age',
    label: 'Sleep unused accounts',
    says: 'Days without sign-in; an administrator must unlock.',
  },
  // NIST advises against expiry (SP 800-63B); it is here because security
  // questionnaires ask for it. Switching it on asks everybody whose password is
  // older at their next sign-in.
  'auth.password_max_age_days': {
    group: 'Account age',
    label: 'Password expiry',
    says: 'Asks for a new one; nobody is refused.',
  },
}

const describe = (s: SettingView): Described => DESCRIBED[s.name] ?? { group: 'Other', label: s.name, says: '' }

const longDate = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${d.getDate()} ${monthName('en', d.getMonth())} ${d.getFullYear()}`
}

export function Settings() {
  const { say } = useStaffText()
  const [settings, setSettings] = useState<SettingView[]>([])
  const [loadError, setLoadError] = useState('')
  const { flash, toast } = useFlash()

  const load = useCallback(async () => {
    try {
      const loaded = await api.settings()
      setSettings(loaded.settings)
      setLoadError('')
    } catch (err) {
      setLoadError(explain(err, 'en', 'common.unreachable'))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const groups = [...new Set(settings.map((s) => describe(s).group))]

  return (
    <>
      <PageHead
        title={say('admin.section.settings')}
        line={
          <>
            {say('settings.install.line')} <code className="install-code-inline">/etc/qms/config.yaml</code>.
          </>
        }
      />
      {toast}
      {loadError && <Notice className="os-gap">{loadError}</Notice>}

      {groups.map((g) => (
        <SettingsCard key={g} title={g}>
          {settings
            .filter((s) => describe(s).group === g)
            .map((s) => (
              <ValueRow key={`${s.name}:${s.value}:${s.setHere}`} s={s} onSaved={load} flash={flash} />
            ))}
        </SettingsCard>
      ))}
    </>
  )
}

function ValueRow({
  s,
  onSaved,
  flash,
}: {
  s: SettingView
  onSaved: () => Promise<void>
  flash: (text: string) => void
}) {
  const { label, says } = describe(s)
  const [value, setValue] = useState(s.value)
  const [busy, setBusy] = useState(false)
  // The server's refusal, beside the box that caused it. It checks the whole set
  // together (an idle timeout longer than the session it bounds), which no single
  // row can know about beforehand.
  const [refusal, setRefusal] = useState('')
  const typed = value.trim()
  const problem = settingCheck(s.kind, typed)
  const dirty = typed !== s.value
  const id = 'set-' + s.name.replace(/\W/g, '-')

  async function send(next: string | null, said: string) {
    setBusy(true)
    setRefusal('')
    try {
      await api.saveSettings({ [s.name]: next })
      await onSaved()
      flash(said)
    } catch (err) {
      setRefusal(explain(err, 'en', 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingRow
      as="form"
      label={<label htmlFor={id}>{label}</label>}
      description={says}
      status={
        s.setHere ? (
          <span className="install-set__source">
            <Chip size="sm" variant="soft" color="accent">
              Set here
            </Chip>{' '}
            {s.updatedBy && <> by {s.updatedBy}</>}
            {s.updatedAt && <>, {longDate(s.updatedAt)}</>}. File: <code className="install-code-inline">{s.fileValue}</code>
          </span>
        ) : undefined
      }
      onSubmit={(e) => {
        e.preventDefault()
        if (!problem && dirty && !busy) void send(typed, `${label}: ${typed}`)
      }}
    >
      <span className="install-set__edit">
        <span className="install-set__box">
          {s.kind === 'choice' ? (
            <ChoiceSelect label={label} value={value} onChange={setValue} options={(s.choices ?? []).map((c) => ({ value: c, label: c }))} />
          ) : (
            <Input
              id={id}
              className="install-input"
              value={value}
              aria-invalid={!!problem}
              aria-describedby={id + '-w'}
              placeholder={{ duration: '12h', count: '5', days: '90' }[s.kind] ?? ''}
              onChange={(e) => {
                setValue(e.target.value)
                setRefusal('')
              }}
            />
          )}
          <span id={id + '-w'} className="install-set__words">
            {problem ? '' : settingInWords(s.kind, typed)}
          </span>
        </span>
        <span className="install-set__acts">
          {dirty && (
            <Button variant="ghost" size="sm" onPress={() => setValue(s.value)}>
              Undo
            </Button>
          )}
          {dirty && (
            <Button type="submit" variant="primary" size="sm" isDisabled={!!problem || busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          )}
          {s.setHere && !dirty && (
            <Button variant="ghost" size="sm" isDisabled={busy} onPress={() => void send(null, `${label}: from file`)}>
              Use file ({s.fileValue})
            </Button>
          )}
        </span>
        {problem && dirty && <span className="install-set__error">{problem}</span>}
        {refusal && <Notice>{refusal}</Notice>}
      </span>
    </SettingRow>
  )
}
